"use client";

import React from "react";
import { ArrowRight, ThermometerSnowflake, Droplets, ShieldCheck, FileDown, PhoneCall } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
export default function EvaporativeHero() {
  const highlights = [{
    icon: ThermometerSnowflake,
    title: "Controlo térmico preciso",
    description: "Redução consistente de temperaturas de gases e produtos em fluxo contínuo."
  }, {
    icon: Droplets,
    title: "Eficiência hídrica",
    description: "Gotículas microatomizadas que evaporam a 100% sem excesso residual."
  }, {
    icon: ShieldCheck,
    title: "Tecnologia Spraying Systems Co.",
    description: "Engenharia comprovada e suporte técnico direto em Portugal desde 1993."
  }];
  return <section id="evaporativecoolinghero" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-20 lg:py-28 border-b border-sidebar-border">
      {/* Decorative technical grid background */}
      <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(circle_at_1px_1px,_var(--color-primary-foreground)_1px,_transparent_0)] bg-[size:32px_32px]" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Main Hero Content */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6">
            <div className="inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                Soluções Térmicas Industriais
              </Badge>
              <span className="text-xs text-primary-foreground/75 font-mono">
                Eurocontrol &bull; Spraying Systems Co.
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-tight">Arrefecimento evaporativo&nbsp;</h1>

            <p className="text-base sm:text-lg text-primary-foreground/85 max-w-2xl font-normal leading-relaxed">A Spraying Systems Co. disponibiliza sistemas completos de arrefecimento evaporativo que mantêm equipamentos críticos a funcionar de forma eficiente em produção de energia, petróleo e gás e instalações industriais. Os nossos bicos de pulverização de elevada eficiência, lanças de engenharia e controladores de pulverização AutoJet® trabalham em conjunto para proporcionar um desempenho de arrefecimento consistente, fiável e eficiente em ambientes exigentes. Estes sistemas são ideais onde temperaturas ambiente elevadas limitam a produção, reduzem a eficiência ou aumentam o risco de sobreaquecimento em arrefecedores de ar de tiragem induzida ou forçada e em torres de arrefecimento de água. </p>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md">
                <Link to="/ContactsPage" className="inline-flex items-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Consulte-nos</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 bg-primary/40 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/CatalogsPage" className="inline-flex items-center gap-2">
                  <FileDown className="size-4" />
                  <span>Ver catálogos técnicos</span>
                </Link>
              </Button>
            </div>

            {/* Fast Quick-facts */}
            <div className="pt-6 border-t border-primary-foreground/15 w-full grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <span className="block text-2xl font-bold font-mono text-secondary-foreground">100%</span>
                <span className="text-xs text-primary-foreground/75">Evaporação sem drenagem</span>
              </div>
              <div>
                <span className="block text-2xl font-bold font-mono text-secondary-foreground">30+ Anos</span>
                <span className="text-xs text-primary-foreground/75">Experiência em Portugal</span>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <span className="block text-2xl font-bold font-mono text-secondary-foreground">AutoJet®</span>
                <span className="text-xs text-primary-foreground/75">Sistemas de controlo</span>
              </div>
            </div>
          </div>

          {/* Right Visual / Feature Cards Box */}
          <div className="lg:col-span-5">
            <div className="bg-sidebar-border/40 backdrop-blur-xs rounded-2xl p-6 sm:p-8 border border-primary-foreground/15 shadow-xl space-y-6">
              <div className="border-b border-primary-foreground/15 pb-4">
                <h2 className="text-lg font-semibold text-primary-foreground">
                  Pilares de desempenho térmico
                </h2>
                <p className="text-xs text-primary-foreground/75 mt-1">
                  Engenharia aplicada a fornos, cimenteiras, celulose e energia.
                </p>
              </div>

              <div className="space-y-4">
                {highlights.map((item, i) => {
                const Icon = item.icon;
                return <motion.div key={item.title} data-index={i} whileHover={{
                  x: 4
                }} transition={{
                  duration: 0.2
                }} className="flex items-start gap-4 p-3 rounded-lg bg-primary/60 border border-primary-foreground/10 transition-colors">
                      <div className="p-2 rounded-md bg-secondary text-secondary-foreground shrink-0 mt-0.5">
                        <Icon className="size-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-primary-foreground">
                          {item.title}
                        </h3>
                        <p className="text-xs text-primary-foreground/80 mt-1 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </motion.div>;
              })}
              </div>

              <div className="pt-2">
                <Button asChild variant="ghost" size="sm" className="w-full justify-between text-xs text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/10">
                  <Link to="/TechnicalSolutionsPage" className="flex items-center justify-between w-full">
                    <span>Explorar casos práticos e bicos compatíveis</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}